/**
 * SpawnRecipeModal Component
 *
 * Nested dialog for creating/editing spawn recipes.
 * Defines the exact composition of a spawn bag (grain, water, bag size, LC volume).
 * Opens on top of SpawnBatchForm when user clicks "+ Add Spawn Recipe".
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Plus } from 'lucide-react'

const GRAIN_TYPES = [
  { value: 'millet', label: 'Millet' },
  { value: 'rye', label: 'Rye' },
  { value: 'wheat', label: 'Wheat' },
  { value: 'oat', label: 'Oat' },
  { value: 'sorghum', label: 'Sorghum' }
]

const initialForm = {
  name: '',
  grain_type: 'millet',
  grain_weight_kg: '',
  water_weight_kg: '',
  bag_weight_kg: '',
  lc_volume_ml: '',
  use_filters: true
}

export default function SpawnRecipeModal({ open, onOpenChange, onSave, recipe = null, saving = false }) {
  const [form, setForm] = useState(initialForm)
  const [error, setError] = useState(null)

  // Populate form when editing existing recipe
  useEffect(() => {
    if (recipe) {
      setForm({
        name: recipe.name || '',
        grain_type: recipe.grain_type || 'millet',
        grain_weight_kg: recipe.grain_weight_kg?.toString() || '',
        water_weight_kg: recipe.water_weight_kg?.toString() || '',
        bag_weight_kg: recipe.bag_weight_kg?.toString() || '',
        lc_volume_ml: recipe.lc_volume_ml?.toString() || '',
        use_filters: recipe.use_filters ?? true
      })
    } else {
      setForm(initialForm)
    }
    setError(null)
  }, [recipe, open])

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  // Calculate pre-sterilisation weight hint
  const grainKg = parseFloat(form.grain_weight_kg) || 0
  const waterKg = parseFloat(form.water_weight_kg) || 0
  const preSterilisationWeight = grainKg + waterKg

  const handleSubmit = (e) => {
    e.preventDefault()
    setError(null)

    if (!form.name.trim()) {
      setError('Recipe name is required')
      return
    }
    if (!form.grain_weight_kg || parseFloat(form.grain_weight_kg) <= 0) {
      setError('Grain weight must be greater than 0')
      return
    }
    if (form.water_weight_kg === '' || parseFloat(form.water_weight_kg) < 0) {
      setError('Water weight is required')
      return
    }
    if (!form.bag_weight_kg || parseFloat(form.bag_weight_kg) <= 0) {
      setError('Bag weight must be greater than 0')
      return
    }

    onSave({
      name: form.name.trim(),
      grain_type: form.grain_type,
      grain_weight_kg: parseFloat(form.grain_weight_kg),
      water_weight_kg: parseFloat(form.water_weight_kg),
      bag_weight_kg: parseFloat(form.bag_weight_kg),
      lc_volume_ml: parseFloat(form.lc_volume_ml) || 10,
      use_filters: form.use_filters
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            {recipe ? 'Edit Spawn Recipe' : 'New Spawn Recipe'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Recipe Name */}
          <div className="space-y-2">
            <Label htmlFor="recipe-name">Recipe Name *</Label>
            <Input
              id="recipe-name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g. Millet 4.5kg"
            />
          </div>

          {/* Grain Type */}
          <div className="space-y-2">
            <Label>Grain Type</Label>
            <Select
              value={form.grain_type}
              onValueChange={(value) => handleChange('grain_type', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GRAIN_TYPES.map(g => (
                  <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Grain Weight & Water Weight */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="grain-weight">Grain Weight (kg) *</Label>
              <div className="relative">
                <Input
                  id="grain-weight"
                  type="number"
                  min={0.1}
                  step={0.01}
                  value={form.grain_weight_kg}
                  onChange={(e) => handleChange('grain_weight_kg', e.target.value)}
                  placeholder="3.0"
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-content-tertiary">kg</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="water-weight">Water Weight (kg) *</Label>
              <div className="relative">
                <Input
                  id="water-weight"
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.water_weight_kg}
                  onChange={(e) => handleChange('water_weight_kg', e.target.value)}
                  placeholder="1.7"
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-content-tertiary">kg</span>
              </div>
            </div>
          </div>

          {/* Pre-sterilisation weight hint */}
          {preSterilisationWeight > 0 && (
            <p className="text-xs text-content-tertiary">
              Pre-sterilisation weight: {preSterilisationWeight.toFixed(2)} kg (grain + water)
            </p>
          )}

          {/* Final Bag Weight */}
          <div className="space-y-2">
            <Label htmlFor="bag-weight">Final Bag Weight (kg) *</Label>
            <div className="relative w-32">
              <Input
                id="bag-weight"
                type="number"
                min={0.1}
                step={0.1}
                value={form.bag_weight_kg}
                onChange={(e) => handleChange('bag_weight_kg', e.target.value)}
                placeholder="4.5"
                className="pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-content-tertiary">kg</span>
            </div>
            <p className="text-xs text-content-tertiary">
              Maps to spawn bag inventory item size
            </p>
          </div>

          {/* LC Volume per Bag */}
          <div className="space-y-2">
            <Label htmlFor="lc-volume">LC Volume per Bag (ml)</Label>
            <div className="relative w-32">
              <Input
                id="lc-volume"
                type="number"
                min={0}
                step={1}
                value={form.lc_volume_ml}
                onChange={(e) => handleChange('lc_volume_ml', e.target.value)}
                placeholder="10"
                className="pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-content-tertiary">ml</span>
            </div>
          </div>

          {/* Filter Patches */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="recipe-filters"
              checked={form.use_filters}
              onCheckedChange={(checked) => handleChange('use_filters', checked)}
            />
            <Label htmlFor="recipe-filters" className="text-sm cursor-pointer">
              Include filter patches
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : (recipe ? 'Update Recipe' : 'Create Recipe')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
